import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Button, Chip, FormControl, InputLabel, LinearProgress, MenuItem, Select, Switch, Tab, Tabs, TextField, Typography } from '@mui/material';
import { toast } from 'react-toastify';
import DataTable from '../../components/design/DataTable.jsx';
import {
  listYoutubeChannels,
  listYoutubeAutomationSettings,
  updateYoutubeAutomationSettings,
  listYoutubeTrends,
  collectYoutubeTrends,
  listYoutubePlans,
  deleteYoutubePlan,
  deleteYoutubeFailedPlans,
  generateYoutubePlans,
  getYoutubePlansGenerateStatus,
  listYoutubeTopicQueue,
  createYoutubeTopicQueue,
  cancelYoutubeTopicQueueItem,
  listYoutubeAutomationRuns,
  triggerYoutubeDailyRun,
  triggerYoutubeAnalyticsAi,
  listYoutubeCampaigns,
  getYoutubeCampaign,
  generateYoutubeCampaign,
  enqueueYoutubeCampaign,
  listYoutubeShortsJobs,
  listYoutubeRepurposeJobs
} from '../../services/contentAiService';

function parseJsonList(v) {
  const s = String(v || '').trim();
  if (!s) return [];
  try {
    const parsed = JSON.parse(s);
    if (Array.isArray(parsed)) return parsed;
    return [];
  } catch (_) {
    return s.split(',').map(x => x.trim()).filter(Boolean);
  }
}

function parseTopicsText(v) {
  const raw = String(v || '').trim();
  if (!raw) return [];
  const out = [];
  const seen = new Set();
  for (const line of raw.split(/\r?\n/g)) {
    for (const part of String(line || '').split(',')) {
      const t = String(part || '').trim().replace(/\s+/g, ' ');
      if (!t) continue;
      if (seen.has(t)) continue;
      seen.add(t);
      out.push(t);
    }
  }
  return out;
}

function normalizeProgressForUi(v) {
  const n = Number(v);
  if (!Number.isFinite(n)) return null;
  if (n <= 1) return Math.max(0, Math.min(100, n * 100));
  return Math.max(0, Math.min(100, n));
}

const TOPIC_QUEUE_PROGRESS_STAGE_ORDER = [
  'idea',
  'research',
  'generate_script',
  'generate_narration',
  'process_audio',
  'storyboard_engine',
  'generate_images',
  'select_scenes',
  'build_timeline',
  'generate_scene_videos',
  'render_video',
  'generate_thumbnail',
  'preview_render',
  'audit',
  'generate_shorts',
  'upload_youtube',
  'publish',
  'analytics_initial'
];

function getTopicQueueProgressPct(row) {
  const normalized = normalizeProgressForUi(row?.metaJson?.progressPct);
  const status = String(row?.status || '');
  const stageKey = String(row?.metaJson?.stageKey || row?.metaJson?.stage || '').trim();
  const index = TOPIC_QUEUE_PROGRESS_STAGE_ORDER.indexOf(stageKey);
  if (!['starting', 'production_running', 'awaiting_approval'].includes(status)) return normalized;
  if (index < 0) return normalized;
  const base = ((index + (status === 'awaiting_approval' ? 0.6 : 0.25)) / TOPIC_QUEUE_PROGRESS_STAGE_ORDER.length) * 100;
  if (normalized == null) return Math.max(1, Math.min(99, base));
  return Math.max(normalized, Math.max(1, Math.min(99, base)));
}

export default function YouTubeAutomationPage() {
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState(0);
  const [channels, setChannels] = useState([]);
  const [selectedChannelId, setSelectedChannelId] = useState('');
  const [trends, setTrends] = useState([]);
  const [plans, setPlans] = useState([]);
  const [topicQueue, setTopicQueue] = useState([]);
  const [runs, setRuns] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [selectedCampaignId, setSelectedCampaignId] = useState('');
  const [campaignDetails, setCampaignDetails] = useState(null);
  const [campaignTheme, setCampaignTheme] = useState('');
  const [campaignMinutes, setCampaignMinutes] = useState(8);
  const [campaignLanguage, setCampaignLanguage] = useState('pt-BR');
  const [shortsJobs, setShortsJobs] = useState([]);
  const [repurposeJobs, setRepurposeJobs] = useState([]);
  const [plansJobId, setPlansJobId] = useState('');
  const [plansJobStatus, setPlansJobStatus] = useState(null);
  const [plansJobStatusLoading, setPlansJobStatusLoading] = useState(false);

  const [topicText, setTopicText] = useState('');
  const [topicLanguage, setTopicLanguage] = useState('pt-BR');
  const [topicMinutes, setTopicMinutes] = useState(4);
  const [topicScriptTemplateKey, setTopicScriptTemplateKey] = useState('yt_script_documentario');

  const [niche, setNiche] = useState('');
  const [scriptGenerationMode, setScriptGenerationMode] = useState('ai');
  const [enabled, setEnabled] = useState(false);
  const [dailyVideoEnabled, setDailyVideoEnabled] = useState(false);
  const [dailyVideoTime, setDailyVideoTime] = useState('09:00');
  const [dailyVideoImageCount, setDailyVideoImageCount] = useState(8);
  const [shortsEnabled, setShortsEnabled] = useState(false);
  const [shortsPerVideo, setShortsPerVideo] = useState(2);
  const [shortsMaxSeconds, setShortsMaxSeconds] = useState(58);
  const [narrationSpeed, setNarrationSpeed] = useState(1.0);
  const [uploadPrivacyStatus, setUploadPrivacyStatus] = useState('private');
  const [categoryId, setCategoryId] = useState('27');
  const [playlistId, setPlaylistId] = useState('');
  const [seoAuto, setSeoAuto] = useState(true);
  const [tagsText, setTagsText] = useState('[]');
  const [hashtagsText, setHashtagsText] = useState('[]');
  const [tiktokWebhookUrl, setTiktokWebhookUrl] = useState('');
  const [reelsWebhookUrl, setReelsWebhookUrl] = useState('');

  const loadChannels = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await listYoutubeChannels();
      setChannels(Array.isArray(resp?.data) ? resp.data : []);
    } catch (e) {
      toast.error(e?.message || 'Falha ao carregar canais');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadSettings = useCallback(async () => {
    if (!selectedChannelId) return;
    setLoading(true);
    try {
      const resp = await listYoutubeAutomationSettings(Number(selectedChannelId));
      const s = resp?.data || null;
      setNiche(s?.niche || '');
      setScriptGenerationMode(s?.scriptGenerationMode || 'ai');
      setEnabled(Boolean(s?.enabled));
      setDailyVideoEnabled(Boolean(s?.dailyVideoEnabled));
      setDailyVideoTime(s?.dailyVideoTime || '09:00');
      setDailyVideoImageCount(Number(s?.dailyVideoImageCount || 8));
      setShortsEnabled(Boolean(s?.shortsEnabled));
      setShortsPerVideo(Number(s?.shortsPerVideo || 2));
      setShortsMaxSeconds(Number(s?.shortsMaxSeconds || 58));
      setNarrationSpeed(s?.narrationSpeed != null ? Number(s.narrationSpeed) : 1.0);
      setUploadPrivacyStatus(s?.uploadPrivacyStatus || 'private');
      setCategoryId(s?.categoryId || '27');
      setPlaylistId(s?.playlistId || '');
      setSeoAuto(s?.seoAuto !== false);
      setTagsText(JSON.stringify(Array.isArray(s?.tagsJson) ? s.tagsJson : (s?.tagsJson || [])));
      setHashtagsText(JSON.stringify(Array.isArray(s?.hashtagsJson) ? s.hashtagsJson : (s?.hashtagsJson || [])));
      setTiktokWebhookUrl(s?.tiktokWebhookUrl || '');
      setReelsWebhookUrl(s?.reelsWebhookUrl || '');
    } catch (e) {
      toast.error(e?.message || 'Falha ao carregar configurações');
    } finally {
      setLoading(false);
    }
  }, [selectedChannelId]);

  const loadData = useCallback(async () => {
    if (!selectedChannelId) return;
    setLoading(true);
    try {
      const [t, p, tq, r, c, sj, rj] = await Promise.all([
        listYoutubeTrends({ youtubeChannelId: Number(selectedChannelId), limit: 20 }),
        listYoutubePlans({ youtubeChannelId: Number(selectedChannelId), limit: 50 }),
        listYoutubeTopicQueue({ youtubeChannelId: Number(selectedChannelId), limit: 200 }),
        listYoutubeAutomationRuns({ youtubeChannelId: Number(selectedChannelId), limit: 50 }),
        listYoutubeCampaigns({ youtubeChannelId: Number(selectedChannelId), limit: 50 }),
        listYoutubeShortsJobs({ youtubeChannelId: Number(selectedChannelId), limit: 50 }),
        listYoutubeRepurposeJobs({ youtubeChannelId: Number(selectedChannelId), limit: 50 })
      ]);
      setTrends(Array.isArray(t?.data) ? t.data : []);
      setPlans(Array.isArray(p?.data) ? p.data : []);
      setTopicQueue(Array.isArray(tq?.data) ? tq.data : []);
      setRuns(Array.isArray(r?.data) ? r.data : []);
      setCampaigns(Array.isArray(c?.data) ? c.data : []);
      setShortsJobs(Array.isArray(sj?.data) ? sj.data : []);
      setRepurposeJobs(Array.isArray(rj?.data) ? rj.data : []);
    } catch (e) {
      toast.error(e?.message || 'Falha ao carregar dados');
    } finally {
      setLoading(false);
    }
  }, [selectedChannelId]);

  useEffect(() => {
    loadChannels();
  }, [loadChannels]);

  useEffect(() => {
    if (!selectedChannelId) return;
    loadSettings();
    loadData();
  }, [selectedChannelId, loadSettings, loadData]);

  const onSave = useCallback(async () => {
    if (!selectedChannelId) return;
    setLoading(true);
    try {
      const payload = {
        niche: niche || null,
        scriptGenerationMode,
        enabled,
        dailyVideoEnabled,
        dailyVideoTime,
        dailyVideoImageCount: Number(dailyVideoImageCount || 8),
        shortsEnabled,
        shortsPerVideo: Number(shortsPerVideo || 2),
        shortsMaxSeconds: Number(shortsMaxSeconds || 58),
        narrationSpeed: narrationSpeed != null ? Number(narrationSpeed) : 1.0,
        uploadPrivacyStatus,
        categoryId,
        playlistId: playlistId || null,
        seoAuto,
        tagsJson: parseJsonList(tagsText),
        hashtagsJson: parseJsonList(hashtagsText),
        tiktokWebhookUrl: tiktokWebhookUrl || null,
        reelsWebhookUrl: reelsWebhookUrl || null
      };
      await updateYoutubeAutomationSettings(Number(selectedChannelId), payload);
      toast.success('Configurações salvas');
      await loadSettings();
    } catch (e) {
      toast.error(e?.message || 'Falha ao salvar');
    } finally {
      setLoading(false);
    }
  }, [selectedChannelId, niche, scriptGenerationMode, enabled, dailyVideoEnabled, dailyVideoTime, dailyVideoImageCount, shortsEnabled, shortsPerVideo, shortsMaxSeconds, narrationSpeed, uploadPrivacyStatus, categoryId, playlistId, seoAuto, tagsText, hashtagsText, tiktokWebhookUrl, reelsWebhookUrl, loadSettings]);

  const onCollectTrends = useCallback(async () => {
    if (!selectedChannelId) return;
    setLoading(true);
    try {
      await collectYoutubeTrends({ youtubeChannelId: Number(selectedChannelId) });
      toast.success('Tendências coletadas');
      await loadData();
    } catch (e) {
      toast.error(e?.message || 'Falha ao coletar tendências');
    } finally {
      setLoading(false);
    }
  }, [selectedChannelId, loadData]);

  const onGeneratePlans = useCallback(async () => {
    if (!selectedChannelId) return;
    setLoading(true);
    try {
      const resp = await generateYoutubePlans({ youtubeChannelId: Number(selectedChannelId), days: 7 });
      if (resp?.data?.queued) {
        setPlansJobId(String(resp?.data?.jobId || ''));
        toast.success('Geração de planos enfileirada.');
        setTab(3);
        setLoading(false);
        return;
      }
      const created = Array.isArray(resp?.data?.created) ? resp.data.created : [];
      const failed = Array.isArray(resp?.data?.failed) ? resp.data.failed : [];
      if (created.length && failed.length) toast.success(`Planos gerados: ${created.length}. Falharam: ${failed.length}.`);
      else if (created.length) toast.success(`Planos gerados: ${created.length}.`);
      else toast.warn('Nenhum plano foi gerado.');
      await loadData();
      setTab(3);
    } catch (e) {
      const failed = Array.isArray(e?.payload?.error?.details?.failed) ? e.payload.error.details.failed : [];
      const first = failed?.[0]?.message ? String(failed[0].message) : '';
      const msg = e?.message || 'Falha ao gerar planos';
      toast.error(first ? `${msg} (${first})` : msg);
    } finally {
      setLoading(false);
    }
  }, [selectedChannelId, loadData]);

  const loadPlansJobStatus = useCallback(async () => {
    if (!plansJobId) return;
    setPlansJobStatusLoading(true);
    try {
      const resp = await getYoutubePlansGenerateStatus(plansJobId);
      const data = resp?.data || null;
      setPlansJobStatus(data);
      const state = data?.state ? String(data.state) : '';
      if (state === 'completed') {
        await loadData();
      }
    } catch (e) {
      if (Number(e?.status || 0) === 401) toast.error('Sessão expirada. Faça login novamente.');
    } finally {
      setPlansJobStatusLoading(false);
    }
  }, [plansJobId, loadData]);

  useEffect(() => {
    if (!plansJobId) return undefined;
    let cancelled = false;
    let interval = null;
    let notified = false;

    const tick = async () => {
      if (cancelled) return;
      try {
        const resp = await getYoutubePlansGenerateStatus(plansJobId);
        const data = resp?.data || null;
        if (cancelled) return;
        setPlansJobStatus(data);
        const state = data?.state ? String(data.state) : '';
        if (state === 'completed' || state === 'failed') {
          try { if (interval) clearInterval(interval); } catch (_) {}
          interval = null;
          await loadData();
        }
      } catch (e) {
        const status = Number(e?.status || 0) || 0;
        if ((status === 401 || status === 403) && !notified) {
          notified = true;
          toast.error(status === 401 ? 'Sessão expirada. Faça login novamente.' : 'Permissão negada.');
        }
        if (status === 401 || status === 403) {
          try { if (interval) clearInterval(interval); } catch (_) {}
          interval = null;
        }
      }
    };

    tick();
    interval = setInterval(tick, 5000);
    return () => {
      cancelled = true;
      try { if (interval) clearInterval(interval); } catch (_) {}
    };
  }, [plansJobId, loadData]);

  const loadTopicQueueOnly = useCallback(async () => {
    if (!selectedChannelId) return;
    try {
      const resp = await listYoutubeTopicQueue({ youtubeChannelId: Number(selectedChannelId), limit: 200 });
      setTopicQueue(Array.isArray(resp?.data) ? resp.data : []);
    } catch (e) {
      toast.error(e?.message || 'Falha ao carregar fila de temas');
    }
  }, [selectedChannelId]);

  useEffect(() => {
    if (!selectedChannelId) return undefined;
    if (tab !== 1) return undefined;
    let cancelled = false;
    let interval = null;
    const tick = async () => {
      if (cancelled) return;
      await loadTopicQueueOnly();
    };
    tick();
    interval = setInterval(tick, 5000);
    return () => {
      cancelled = true;
      try { if (interval) clearInterval(interval); } catch (_) {}
    };
  }, [tab, selectedChannelId, loadTopicQueueOnly]);

  const onCreateTopicQueue = useCallback(async () => {
    if (!selectedChannelId) return;
    const topics = parseTopicsText(topicText);
    if (!topics.length) {
      toast.warn('Informe ao menos 1 tema');
      return;
    }
    setLoading(true);
    try {
      const payload = {
        youtubeChannelId: Number(selectedChannelId),
        topics,
        language: String(topicLanguage || 'pt-BR').trim() || 'pt-BR',
        minutes: Number(topicMinutes || 8) || 8,
        scriptTemplateKey: String(topicScriptTemplateKey || '').trim() || null
      };
      const resp = await createYoutubeTopicQueue(payload);
      const createdCount = Array.isArray(resp?.data) ? resp.data.length : (resp?.data?.createdCount != null ? Number(resp.data.createdCount) : topics.length);
      toast.success(`Temas adicionados à fila: ${createdCount}`);
      setTopicText('');
      await loadTopicQueueOnly();
      setTab(1);
    } catch (e) {
      toast.error(e?.message || 'Falha ao cadastrar temas');
    } finally {
      setLoading(false);
    }
  }, [selectedChannelId, topicText, topicLanguage, topicMinutes, topicScriptTemplateKey, loadTopicQueueOnly]);

  const onRunDaily = useCallback(async () => {
    if (!selectedChannelId) return;
    setLoading(true);
    try {
      await triggerYoutubeDailyRun({ youtubeChannelId: Number(selectedChannelId) });
      toast.success('Execução diária enfileirada');
      await loadData();
    } catch (e) {
      toast.error(e?.message || 'Falha ao enfileirar');
    } finally {
      setLoading(false);
    }
  }, [selectedChannelId, loadData]);

  const onRunAnalyticsAi = useCallback(async () => {
    if (!selectedChannelId) return;
    setLoading(true);
    try {
      await triggerYoutubeAnalyticsAi({ youtubeChannelId: Number(selectedChannelId) });
      toast.success('AnalyticsAI executado');
      await loadData();
    } catch (e) {
      toast.error(e?.message || 'Falha ao executar AnalyticsAI');
    } finally {
      setLoading(false);
    }
  }, [selectedChannelId, loadData]);

  const loadCampaignDetails = useCallback(async (campaignId) => {
    const id = Number(campaignId || 0) || null;
    if (!id) return;
    setLoading(true);
    try {
      const resp = await getYoutubeCampaign(id);
      setCampaignDetails(resp?.data || null);
      setSelectedCampaignId(String(id));
    } catch (e) {
      toast.error(e?.message || 'Falha ao carregar campanha');
    } finally {
      setLoading(false);
    }
  }, []);

  const onGenerateCampaign = useCallback(async () => {
    if (!selectedChannelId) return;
    const theme = String(campaignTheme || '').trim();
    if (!theme) {
      toast.warn('Informe o tema da série');
      return;
    }
    setLoading(true);
    try {
      const payload = {
        youtubeChannelId: Number(selectedChannelId),
        theme,
        count: 20,
        language: String(campaignLanguage || 'pt-BR').trim() || 'pt-BR',
        minutes: Number(campaignMinutes || 8) || 8
      };
      const resp = await generateYoutubeCampaign(payload);
      const campaignId = resp?.data?.campaignId || null;
      toast.success(`Campanha gerada. Produções: ${resp?.data?.itemsCount || 0}`);
      await loadData();
      if (campaignId) await loadCampaignDetails(campaignId);
      setTab(7);
    } catch (e) {
      toast.error(e?.message || 'Falha ao gerar campanha');
    } finally {
      setLoading(false);
    }
  }, [selectedChannelId, campaignTheme, campaignLanguage, campaignMinutes, loadCampaignDetails, loadData]);

  const onEnqueueCampaign = useCallback(async (campaignId) => {
    const id = Number(campaignId || 0) || null;
    if (!id) return;
    setLoading(true);
    try {
      const resp = await enqueueYoutubeCampaign(id);
      toast.success(`Enfileirado: ${resp?.data?.queued ?? 0}. Ignorados: ${resp?.data?.skipped ?? 0}`);
      await loadCampaignDetails(id);
    } catch (e) {
      toast.error(e?.message || 'Falha ao enfileirar campanha');
    } finally {
      setLoading(false);
    }
  }, [loadCampaignDetails]);

  const columnsTrends = [
    { field: 'collectedAt', headerName: 'Coletado', width: 190, valueGetter: (p) => (p?.row?.collectedAt ? new Date(p.row.collectedAt).toLocaleString() : '-') },
    { field: 'count', headerName: 'Itens', width: 90, valueGetter: (p) => (Array.isArray(p?.row?.trendsJson?.items) ? p.row.trendsJson.items.length : 0) },
    { field: 'sample', headerName: 'Exemplo', flex: 1, minWidth: 380, valueGetter: (p) => (p?.row?.trendsJson?.items?.[0]?.title || '-') }
  ];

  const columnsPlans = [
    { field: 'planDate', headerName: 'Data', width: 120 },
    { field: 'status', headerName: 'Status', width: 120 },
    { field: 'topic', headerName: 'Tema', flex: 1, minWidth: 360, valueGetter: (p) => (p?.row?.planJson?.plan?.topic || p?.row?.planJson?.script?.title || '-') },
    {
      field: 'actions',
      headerName: 'Ações',
      width: 140,
      renderCell: (p) => {
        const st = String(p?.row?.status || '');
        if (st !== 'failed') return '-';
        return (
          <Button
            size="small"
            variant="outlined"
            color="error"
            onClick={async () => {
              const id = p?.row?.id;
              if (!id) return;
              const ok = window.confirm(`Excluir o plano #${id} (failed)?`);
              if (!ok) return;
              setLoading(true);
              try {
                await deleteYoutubePlan(id);
                toast.success('Plano excluído');
                await loadData();
              } catch (e) {
                toast.error(e?.message || 'Falha ao excluir plano');
              } finally {
                setLoading(false);
              }
            }}
          >
            Excluir
          </Button>
        );
      }
    }
  ];

  const columnsTopicQueue = [
    { field: 'id', headerName: '#', width: 90 },
    { field: 'status', headerName: 'Status', width: 160, renderCell: (p) => <Chip size="small" label={String(p?.row?.status || '-')} /> },
    { field: 'topic', headerName: 'Tema', flex: 1, minWidth: 360 },
    { field: 'productionId', headerName: 'Produção', width: 140, renderCell: (p) => {
      const id = p?.row?.productionId != null ? Number(p.row.productionId) : null;
      if (!id) return '-';
      return (
        <Button size="small" variant="text" onClick={() => { window.location.href = `/production/${id}`; }}>
          #{id}
        </Button>
      );
    } },
    { field: 'stage', headerName: 'Etapa', width: 180, valueGetter: (p) => (p?.row?.metaJson?.stageKey || p?.row?.metaJson?.stage || '-') },
    { field: 'progress', headerName: 'Progresso', width: 120, valueGetter: (p) => {
      const pct = getTopicQueueProgressPct(p?.row);
      if (pct == null) return '-';
      return `${Math.max(0, Math.min(100, Math.round(Number(pct) || 0)))}%`;
    } },
    { field: 'createdAt', headerName: 'Criado', width: 190, valueGetter: (p) => (p?.row?.createdAt ? new Date(p.row.createdAt).toLocaleString() : '-') },
    { field: 'error', headerName: 'Erro', flex: 1, minWidth: 260, valueGetter: (p) => (p?.row?.errorMessage || '-') },
    {
      field: 'actions',
      headerName: 'Ações',
      width: 260,
      renderCell: (p) => {
        const row = p?.row || {};
        const queueId = Number(row?.id || 0) || 0;
        const productionId = row?.productionId != null ? Number(row.productionId) : null;
        const cancellable = ['queued', 'starting', 'production_running', 'awaiting_approval'].includes(String(row?.status || ''));
        return (
          <Box display="flex" gap={1} flexWrap="wrap">
            {productionId ? (
              <Button size="small" variant="text" onClick={() => { window.location.href = `/production/${productionId}`; }}>
                Abrir
              </Button>
            ) : null}
            {cancellable ? (
              <Button
                size="small"
                color="error"
                variant="outlined"
                onClick={async () => {
                  if (!queueId) return;
                  const ok = window.confirm(`Encerrar o item #${queueId} e liberar o próximo tema da fila?`);
                  if (!ok) return;
                  setLoading(true);
                  try {
                    await cancelYoutubeTopicQueueItem(queueId);
                    toast.success('Item encerrado e fila atualizada.');
                    await loadData();
                  } catch (e) {
                    toast.error(e?.message || 'Falha ao encerrar item da fila');
                  } finally {
                    setLoading(false);
                  }
                }}
              >
                Encerrar
              </Button>
            ) : null}
          </Box>
        );
      }
    }
  ];

  const columnsCampaigns = [
    { field: 'id', headerName: '#', width: 90 },
    { field: 'status', headerName: 'Status', width: 120, valueGetter: (p) => (p?.row?.status || '-') },
    { field: 'theme', headerName: 'Tema', flex: 1, minWidth: 320, valueGetter: (p) => (p?.row?.theme || '-') },
    { field: 'ideasCount', headerName: 'Ideias', width: 110, valueGetter: (p) => (p?.row?.ideasCount ?? p?.row?.ideas_count ?? '-') },
    { field: 'createdAt', headerName: 'Criado', width: 190, valueGetter: (p) => (p?.row?.createdAt ? new Date(p.row.createdAt).toLocaleString() : '-') },
    {
      field: 'actions',
      headerName: 'Ações',
      width: 220,
      renderCell: (p) => (
        <Box display="flex" gap={1}>
          <Button size="small" variant="outlined" onClick={() => loadCampaignDetails(p?.row?.id)}>Ver</Button>
          <Button size="small" variant="contained" onClick={() => onEnqueueCampaign(p?.row?.id)} disabled={!p?.row?.id}>Enfileirar</Button>
        </Box>
      )
    }
  ];

  const columnsCampaignItems = [
    { field: 'itemIndex', headerName: '#', width: 70, valueGetter: (p) => (p?.row?.itemIndex ?? p?.row?.item_index ?? '-') },
    { field: 'status', headerName: 'Status', width: 120, valueGetter: (p) => (p?.row?.status || '-') },
    { field: 'idea', headerName: 'Ideia', flex: 1, minWidth: 380, valueGetter: (p) => (p?.row?.idea || '-') },
    {
      field: 'productionId',
      headerName: 'Produção',
      width: 140,
      renderCell: (p) => {
        const id = p?.row?.productionId != null ? Number(p.row.productionId) : null;
        if (!id) return '-';
        return (
          <Button size="small" variant="text" onClick={() => { window.location.href = `/production/${id}`; }}>
            #{id}
          </Button>
        );
      }
    }
  ];

  const columnsRuns = [
    { field: 'id', headerName: '#', width: 90 },
    { field: 'kind', headerName: 'Tipo', width: 150 },
    { field: 'status', headerName: 'Status', width: 120 },
    { field: 'productionId', headerName: 'Produção', width: 120, valueGetter: (p) => (p?.row?.metaJson?.productionId || '-') },
    { field: 'stage', headerName: 'Etapa', width: 160, valueGetter: (p) => (p?.row?.metaJson?.stageKey || p?.row?.metaJson?.stage || '-') },
    { field: 'youtubeUploadId', headerName: 'Upload', width: 120, valueGetter: (p) => (p?.row?.metaJson?.youtubeUploadId || '-') },
    { field: 'startedAt', headerName: 'Início', width: 190, valueGetter: (p) => (p?.row?.startedAt ? new Date(p.row.startedAt).toLocaleString() : '-') },
    { field: 'finishedAt', headerName: 'Fim', width: 190, valueGetter: (p) => (p?.row?.finishedAt ? new Date(p.row.finishedAt).toLocaleString() : '-') },
    { field: 'error', headerName: 'Erro', flex: 1, minWidth: 320, valueGetter: (p) => (p?.row?.errorMessage || '-') }
  ];

  const columnsShorts = [
    { field: 'id', headerName: '#', width: 90 },
    { field: 'status', headerName: 'Status', width: 120 },
    { field: 'sourceVideoId', headerName: 'Vídeo', width: 120 },
    { field: 'outputs', headerName: 'Outputs', width: 120, valueGetter: (p) => (Array.isArray(p?.row?.outputAssetIds) ? p.row.outputAssetIds.length : 0) },
    { field: 'updatedAt', headerName: 'Atualizado', width: 190, valueGetter: (p) => (p?.row?.updatedAt ? new Date(p.row.updatedAt).toLocaleString() : '-') },
    { field: 'lastError', headerName: 'Erro', flex: 1, minWidth: 320, valueGetter: (p) => (p?.row?.lastError || '-') }
  ];

  const columnsRepurpose = [
    { field: 'id', headerName: '#', width: 90 },
    { field: 'platform', headerName: 'Plataforma', width: 120 },
    { field: 'status', headerName: 'Status', width: 120 },
    { field: 'sourceVideoId', headerName: 'Vídeo', width: 120 },
    { field: 'outputAssetId', headerName: 'Asset', width: 120, valueGetter: (p) => (p?.row?.outputAssetId || '-') },
    { field: 'updatedAt', headerName: 'Atualizado', width: 190, valueGetter: (p) => (p?.row?.updatedAt ? new Date(p.row.updatedAt).toLocaleString() : '-') },
    { field: 'lastError', headerName: 'Erro', flex: 1, minWidth: 320, valueGetter: (p) => (p?.row?.lastError || '-') }
  ];

  const currentChannelLabel = useMemo(() => {
    const c = channels.find(x => String(x.id) === String(selectedChannelId));
    return c ? (c.name || c.channelId || `Canal ${c.id}`) : '';
  }, [channels, selectedChannelId]);

  const activeTopic = useMemo(() => {
    const items = Array.isArray(topicQueue) ? topicQueue : [];
    const active = items.filter(x => ['starting', 'production_running', 'awaiting_approval'].includes(String(x?.status || ''))).sort((a, b) => Number(a?.id || 0) - Number(b?.id || 0));
    return active[0] || null;
  }, [topicQueue]);

  return (
    <Box p={2} display="grid" gap={2}>
      <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
        <Typography variant="h5">Automação YouTube</Typography>
        <Box display="flex" gap={2} flexWrap="wrap">
          <Button variant="outlined" onClick={loadData} disabled={loading || !selectedChannelId}>Atualizar</Button>
          <Button variant="outlined" onClick={onCollectTrends} disabled={loading || !selectedChannelId}>Coletar tendências</Button>
          <Button variant="outlined" onClick={onGeneratePlans} disabled={loading || !selectedChannelId}>Gerar plano (7 dias)</Button>
          <Button variant="contained" onClick={onRunDaily} disabled={loading || !selectedChannelId}>Rodar diário agora</Button>
          <Button variant="contained" onClick={onRunAnalyticsAi} disabled={loading || !selectedChannelId}>AnalyticsAI</Button>
        </Box>
      </Box>

      <FormControl fullWidth>
        <InputLabel>Canal</InputLabel>
        <Select label="Canal" value={selectedChannelId} onChange={(e) => setSelectedChannelId(e.target.value)}>
          <MenuItem value="">Selecione</MenuItem>
          {channels.map((c) => (
            <MenuItem key={c.id} value={String(c.id)}>{c.name || c.channelId || `Canal ${c.id}`}</MenuItem>
          ))}
        </Select>
      </FormControl>

      <Tabs value={tab} onChange={(_, v) => setTab(v)}>
        <Tab label="Config" />
        <Tab label="Temas" />
        <Tab label="Tendências" />
        <Tab label="Planos" />
        <Tab label="Runs" />
        <Tab label="Shorts" />
        <Tab label="Repurpose" />
        <Tab label="Campanhas" />
      </Tabs>

      {tab === 0 && (
        <Box display="grid" gap={2} gridTemplateColumns="repeat(12, 1fr)">
          <Box gridColumn="span 12">
            <Typography variant="subtitle1">Canal: {currentChannelLabel || '-'}</Typography>
          </Box>

          <Box gridColumn="span 6" display="flex" alignItems="center" gap={1}>
            <Switch checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />
            <Typography>Automação ativada</Typography>
          </Box>
          <Box gridColumn="span 6" display="flex" alignItems="center" gap={1}>
            <Switch checked={seoAuto} onChange={(e) => setSeoAuto(e.target.checked)} />
            <Typography>SEO automático</Typography>
          </Box>

          <Box gridColumn="span 12">
            <TextField label="Nicho" fullWidth value={niche} onChange={(e) => setNiche(e.target.value)} />
          </Box>

          <Box gridColumn="span 12">
            <FormControl fullWidth>
              <InputLabel id="yt-script-mode-label">Roteiro</InputLabel>
              <Select
                labelId="yt-script-mode-label"
                label="Roteiro"
                value={scriptGenerationMode}
                onChange={(e) => setScriptGenerationMode(e.target.value)}
              >
                <MenuItem value="ai">Gerar via IA (providers configurados)</MenuItem>
                <MenuItem value="manual">Manual (colar JSON do roteiro)</MenuItem>
              </Select>
            </FormControl>
          </Box>

          <Box gridColumn="span 4" display="flex" alignItems="center" gap={1}>
            <Switch checked={dailyVideoEnabled} onChange={(e) => setDailyVideoEnabled(e.target.checked)} />
            <Typography>Vídeo diário</Typography>
          </Box>
          <Box gridColumn="span 4">
            <TextField label="Hora diária (HH:MM)" fullWidth value={dailyVideoTime} onChange={(e) => setDailyVideoTime(e.target.value)} />
          </Box>
          <Box gridColumn="span 4">
            <TextField label="Qtde imagens" type="number" fullWidth value={dailyVideoImageCount} onChange={(e) => setDailyVideoImageCount(Number(e.target.value))} />
          </Box>

          <Box gridColumn="span 4" display="flex" alignItems="center" gap={1}>
            <Switch checked={shortsEnabled} onChange={(e) => setShortsEnabled(e.target.checked)} />
            <Typography>Shorts automáticos</Typography>
          </Box>
          <Box gridColumn="span 4">
            <TextField label="Shorts por vídeo" type="number" fullWidth value={shortsPerVideo} onChange={(e) => setShortsPerVideo(Number(e.target.value))} />
          </Box>
          <Box gridColumn="span 4">
            <TextField label="Máx seg" type="number" fullWidth value={shortsMaxSeconds} onChange={(e) => setShortsMaxSeconds(Number(e.target.value))} />
          </Box>
          <Box gridColumn="span 4">
            <TextField
              label="Velocidade da narração (0.5–2.0)"
              type="number"
              fullWidth
              value={narrationSpeed}
              onChange={(e) => setNarrationSpeed(Number(e.target.value))}
              inputProps={{ min: 0.5, max: 2, step: 0.05 }}
            />
          </Box>

          <Box gridColumn="span 4">
            <TextField label="Privacidade (public|unlisted|private)" fullWidth value={uploadPrivacyStatus} onChange={(e) => setUploadPrivacyStatus(e.target.value)} />
          </Box>
          <Box gridColumn="span 4">
            <TextField label="Category ID" fullWidth value={categoryId} onChange={(e) => setCategoryId(e.target.value)} />
          </Box>
          <Box gridColumn="span 4">
            <TextField label="Playlist ID" fullWidth value={playlistId} onChange={(e) => setPlaylistId(e.target.value)} />
          </Box>

          <Box gridColumn="span 6">
            <TextField label="Tags (JSON array ou CSV)" fullWidth value={tagsText} onChange={(e) => setTagsText(e.target.value)} />
          </Box>
          <Box gridColumn="span 6">
            <TextField label="Hashtags (JSON array ou CSV)" fullWidth value={hashtagsText} onChange={(e) => setHashtagsText(e.target.value)} />
          </Box>

          <Box gridColumn="span 6">
            <TextField label="TikTok webhook URL" fullWidth value={tiktokWebhookUrl} onChange={(e) => setTiktokWebhookUrl(e.target.value)} />
          </Box>
          <Box gridColumn="span 6">
            <TextField label="Reels webhook URL" fullWidth value={reelsWebhookUrl} onChange={(e) => setReelsWebhookUrl(e.target.value)} />
          </Box>

          <Box gridColumn="span 12" display="flex" justifyContent="flex-end">
            <Button variant="contained" onClick={onSave} disabled={loading || !selectedChannelId}>Salvar</Button>
          </Box>
        </Box>
      )}

      {tab === 1 && (
        <Box display="grid" gap={2}>
          <Box p={1.5} border="1px solid rgba(0,0,0,0.12)" borderRadius={1} display="grid" gap={1.25}>
            <Typography variant="subtitle2">Cadastrar temas (fila)</Typography>
            <TextField
              label="Temas (1 por linha ou separado por vírgula)"
              multiline
              minRows={5}
              fullWidth
              value={topicText}
              onChange={(e) => setTopicText(e.target.value)}
            />
            <Box display="grid" gap={1.25} gridTemplateColumns="repeat(12, 1fr)">
              <Box gridColumn="span 4">
                <TextField label="Idioma" fullWidth value={topicLanguage} onChange={(e) => setTopicLanguage(e.target.value)} />
              </Box>
              <Box gridColumn="span 4">
                <TextField label="Minutos" type="number" fullWidth value={topicMinutes} onChange={(e) => setTopicMinutes(Number(e.target.value))} />
              </Box>
              <Box gridColumn="span 4">
                <TextField label="Template do roteiro" fullWidth value={topicScriptTemplateKey} onChange={(e) => setTopicScriptTemplateKey(e.target.value)} />
              </Box>
            </Box>
            <Box display="flex" gap={1} justifyContent="flex-end">
              <Button variant="outlined" onClick={loadTopicQueueOnly} disabled={loading || !selectedChannelId}>Atualizar fila</Button>
              <Button variant="contained" onClick={onCreateTopicQueue} disabled={loading || !selectedChannelId}>Adicionar à fila</Button>
            </Box>
          </Box>

          {activeTopic && (
            <Box p={1.5} border="1px solid rgba(0,0,0,0.12)" borderRadius={1} display="grid" gap={0.75}>
              <Typography variant="subtitle2">Processando agora</Typography>
              <Typography variant="body2">
                #{Number(activeTopic.id)} — {String(activeTopic.topic || '-')}{activeTopic?.productionId ? ` (produção #${Number(activeTopic.productionId)})` : ''}
              </Typography>
              <Typography variant="body2">
                Status: {String(activeTopic.status || '-')}{activeTopic?.metaJson?.stageKey ? ` | Etapa: ${String(activeTopic.metaJson.stageKey)}` : ''}
              </Typography>
              {getTopicQueueProgressPct(activeTopic) != null && (
                <Box display="grid" gap={0.5}>
                  <LinearProgress variant="determinate" value={getTopicQueueProgressPct(activeTopic) || 0} />
                  <Typography variant="caption">{Math.round(getTopicQueueProgressPct(activeTopic) || 0)}%</Typography>
                </Box>
              )}
              <Box display="flex" gap={1} flexWrap="wrap">
                {activeTopic?.productionId ? (
                  <Button size="small" variant="text" onClick={() => { window.location.href = `/production/${Number(activeTopic.productionId)}`; }}>
                    Abrir produção
                  </Button>
                ) : null}
                {['starting', 'production_running', 'awaiting_approval'].includes(String(activeTopic?.status || '')) ? (
                  <Button
                    size="small"
                    color="error"
                    variant="outlined"
                    onClick={async () => {
                      const queueId = Number(activeTopic?.id || 0) || 0;
                      if (!queueId) return;
                      const ok = window.confirm(`Encerrar o item atual #${queueId} e iniciar o próximo tema da fila?`);
                      if (!ok) return;
                      setLoading(true);
                      try {
                        await cancelYoutubeTopicQueueItem(queueId);
                        toast.success('Produção atual encerrada. Verificando próximo tema.');
                        await loadData();
                      } catch (e) {
                        toast.error(e?.message || 'Falha ao encerrar a produção atual');
                      } finally {
                        setLoading(false);
                      }
                    }}
                  >
                    Reprovar e encerrar
                  </Button>
                ) : null}
              </Box>
            </Box>
          )}

          <DataTable rows={topicQueue} loading={loading} columns={columnsTopicQueue} getRowId={(r) => r.id} />
        </Box>
      )}

      {tab === 2 && (
        <DataTable rows={trends} loading={loading} columns={columnsTrends} getRowId={(r) => r.id} />
      )}

      {tab === 3 && (
        <Box display="grid" gap={1}>
          <Box display="flex" justifyContent="flex-end" gap={1}>
            <Button
              variant="outlined"
              color="error"
              onClick={async () => {
                if (!selectedChannelId) return;
                const ok = window.confirm('Excluir todos os planos com status failed deste canal?');
                if (!ok) return;
                setLoading(true);
                try {
                  const resp = await deleteYoutubeFailedPlans({ youtubeChannelId: Number(selectedChannelId), limit: 1000 });
                  const deletedCount = Number(resp?.data?.deletedCount || 0) || 0;
                  toast.success(`Planos failed excluídos: ${deletedCount}`);
                  await loadData();
                } catch (e) {
                  toast.error(e?.message || 'Falha ao excluir planos failed');
                } finally {
                  setLoading(false);
                }
              }}
              disabled={loading || !selectedChannelId}
            >
              Excluir failed
            </Button>
          </Box>
          {plansJobId && (
            <Box p={1.5} border="1px solid rgba(0,0,0,0.12)" borderRadius={1} display="grid" gap={0.75}>
              <Typography variant="subtitle2">Geração de planos</Typography>
              <Typography variant="body2">
                Job: {plansJobId || '-'} | Status: {plansJobStatus?.state || '...'}
              </Typography>
              <Typography variant="body2">
                Fila (ai): waiting {plansJobStatus?.queue?.waiting ?? '-'} | active {plansJobStatus?.queue?.active ?? '-'} | delayed {plansJobStatus?.queue?.delayed ?? '-'}
              </Typography>
              {typeof plansJobStatus?.progress === 'object' && plansJobStatus?.progress && (
                <Box display="grid" gap={0.5}>
                  <LinearProgress variant="determinate" value={Number(plansJobStatus.progress.pct || 0) || 0} />
                  <Typography variant="caption">
                    {Number(plansJobStatus?.progress?.pct || 0) || 0}% ({plansJobStatus?.progress?.current || 0}/{plansJobStatus?.progress?.total || 0}) {plansJobStatus?.progress?.planDate ? `- ${plansJobStatus.progress.planDate}` : ''}
                  </Typography>
                </Box>
              )}
              {plansJobStatus?.failedReason && (
                <Typography variant="body2" color="error">Erro: {String(plansJobStatus.failedReason)}</Typography>
              )}
              <Box display="flex" gap={1} justifyContent="flex-end">
                <Button variant="outlined" size="small" onClick={loadPlansJobStatus} disabled={plansJobStatusLoading}>Atualizar status</Button>
              </Box>
            </Box>
          )}
          <DataTable rows={plans} loading={loading} columns={columnsPlans} getRowId={(r) => r.id} />
        </Box>
      )}

      {tab === 4 && (
        <DataTable rows={runs} loading={loading} columns={columnsRuns} getRowId={(r) => r.id} />
      )}

      {tab === 5 && (
        <DataTable rows={shortsJobs} loading={loading} columns={columnsShorts} getRowId={(r) => r.id} />
      )}

      {tab === 6 && (
        <DataTable rows={repurposeJobs} loading={loading} columns={columnsRepurpose} getRowId={(r) => r.id} />
      )}

      {tab === 7 && (
        <Box display="grid" gap={2}>
          <Box p={1.5} border="1px solid rgba(0,0,0,0.12)" borderRadius={1} display="grid" gap={1.25}>
            <Typography variant="subtitle2">Campaign Generator (séries)</Typography>
            <Box display="grid" gap={1.25} gridTemplateColumns="repeat(12, 1fr)">
              <Box gridColumn="span 12">
                <TextField label="Tema da série" fullWidth value={campaignTheme} onChange={(e) => setCampaignTheme(e.target.value)} />
              </Box>
              <Box gridColumn="span 4">
                <TextField label="Idioma" fullWidth value={campaignLanguage} onChange={(e) => setCampaignLanguage(e.target.value)} />
              </Box>
              <Box gridColumn="span 4">
                <TextField label="Minutos" type="number" fullWidth value={campaignMinutes} onChange={(e) => setCampaignMinutes(Number(e.target.value))} />
              </Box>
              <Box gridColumn="span 4">
                <TextField label="Quantidade" fullWidth value="20" disabled />
              </Box>
            </Box>
            <Box display="flex" gap={1} justifyContent="flex-end">
              <Button variant="outlined" onClick={loadData} disabled={loading || !selectedChannelId}>Atualizar</Button>
              <Button variant="contained" onClick={onGenerateCampaign} disabled={loading || !selectedChannelId}>Gerar 20 produções</Button>
            </Box>
          </Box>

          <DataTable rows={campaigns} loading={loading} columns={columnsCampaigns} getRowId={(r) => r.id} />

          {campaignDetails?.id && (
            <Box p={1.5} border="1px solid rgba(0,0,0,0.12)" borderRadius={1} display="grid" gap={1}>
              <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
                <Typography variant="subtitle2">Campanha #{campaignDetails.id} — {campaignDetails.theme || '-'}</Typography>
                <Button variant="contained" onClick={() => onEnqueueCampaign(campaignDetails.id)} disabled={loading}>Enfileirar lote</Button>
              </Box>
              <DataTable rows={Array.isArray(campaignDetails?.items) ? campaignDetails.items : []} loading={loading} columns={columnsCampaignItems} getRowId={(r) => r.id} />
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
}
